import { useCallback, useEffect, useState } from 'react';

interface AudioCacheEntry {
  id: string;
  url: string;
  blob: Blob;
  timestamp: number;
}

const DB_NAME = 'FocusMusicCache';
const DB_VERSION = 1;
const STORE_NAME = 'audioTracks';
const CACHE_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 7 days

export const useAudioCache = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [cacheSize, setCacheSize] = useState(0);

  useEffect(() => {
    setIsSupported('indexedDB' in window);
  }, []);

  const openDB = useCallback((): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }, []);

  const cacheAudio = useCallback(async (id: string, url: string): Promise<boolean> => {
    if (!isSupported) return false;

    try {
      const response = await fetch(url);
      if (!response.ok) return false;

      const blob = await response.blob();
      const db = await openDB();
      
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const entry: AudioCacheEntry = {
        id,
        url,
        blob,
        timestamp: Date.now()
      };
      
      await new Promise<void>((resolve, reject) => {
        const request = store.put(entry);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      return true;
    } catch (error) {
      // Silent fail - audio caching is non-critical
      return false;
    }
  }, [isSupported, openDB]);

  const getCachedAudio = useCallback(async (id: string): Promise<string | null> => {
    if (!isSupported) return null;

    try {
      const db = await openDB();
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      
      const entry = await new Promise<AudioCacheEntry | null>((resolve, reject) => {
        const request = store.get(id);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
      });

      if (!entry) return null;

      // Check if cache entry is expired
      if (Date.now() - entry.timestamp > CACHE_EXPIRY) {
        await deleteCachedAudio(id);
        return null;
      }

      return URL.createObjectURL(entry.blob);
    } catch (error) {
      // Silent fail - audio caching is non-critical
      return null;
    }
  }, [isSupported, openDB]);

  const deleteCachedAudio = useCallback(async (id: string): Promise<void> => {
    if (!isSupported) return;

    try {
      const db = await openDB();
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      await new Promise<void>((resolve, reject) => {
        const request = store.delete(id);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      // Silent fail - audio caching is non-critical
    }
  }, [isSupported, openDB]);

  const clearExpiredCache = useCallback(async (): Promise<void> => {
    if (!isSupported) return;

    try {
      const db = await openDB();
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('timestamp');
      
      const cutoffTime = Date.now() - CACHE_EXPIRY;
      const range = IDBKeyRange.upperBound(cutoffTime);
      
      await new Promise<void>((resolve, reject) => {
        const request = index.openCursor(range);
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          } else {
            resolve();
          }
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      // Silent fail - cache cleanup is non-critical
    }
  }, [isSupported, openDB]);

  const getCacheSize = useCallback(async (): Promise<number> => {
    if (!isSupported) return 0;

    try {
      const db = await openDB();
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      
      const count = await new Promise<number>((resolve, reject) => {
        const request = store.count();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      setCacheSize(count);
      return count;
    } catch (error) {
      // Silent fail - cache size is non-critical
      return 0;
    }
  }, [isSupported, openDB]);

  // Clean up expired entries on mount
  useEffect(() => {
    if (isSupported) {
      clearExpiredCache();
      getCacheSize();
    }
  }, [isSupported, clearExpiredCache, getCacheSize]);

  return {
    isSupported,
    cacheSize,
    cacheAudio,
    getCachedAudio,
    deleteCachedAudio,
    clearExpiredCache,
    getCacheSize
  };
};